import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ProductService } from 'src/app/core/services/product.service';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import { lastValueFrom } from 'rxjs';

interface PrintBarcodeModalData {
  shelf: any;
  productName: string;
  variantId: string;
}

interface BarcodeItem {
  serialNumber: string;
  shelfCode: string;
  barcode?: string;
  quantity?: number;
  shelfId?: string;
  _id?: string;
  reason?: string;
}

@Component({
  selector: 'mega-store-factory-print-barcode-modal',
  templateUrl: './print-barcode-modal.component.html',
  styleUrls: ['./print-barcode-modal.component.scss']
})
export class PrintBarcodeModalComponent {
  editingItem: BarcodeItem | null = null;
  originalQuantities: { [key: string]: number } = {};
  barcodeItems: BarcodeItem[] = [];
  quantitiesUpdated: boolean = false;

  constructor(
    public dialogRef: MatDialogRef<PrintBarcodeModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: PrintBarcodeModalData,
    private productService: ProductService,
    private toasterService: ToasterService,
    private spinnerService: SpinnerService,
    private translateService: TranslateService
  ) {
    this.barcodeItems = this.initializeBarcodeItems();
    this.barcodeItems.forEach(item => {
      this.originalQuantities[item.serialNumber] = item.quantity || 0;
    });
  }

  getBarcodeItems(): BarcodeItem[] {
    return this.barcodeItems;
  }

  private initializeBarcodeItems(): BarcodeItem[] {
    // If we have the actual shelf data with barcode items (from user's provided structure)
    if (this.data.shelf.shelves && Array.isArray(this.data.shelf.shelves)) {
      return this.data.shelf.shelves.map((item: any) => ({
        serialNumber: item.serialNumber,
        shelfCode: item.shelfCode,
        barcode: item.barcode,
        quantity: item.quantity || 0,
        shelfId: item.shelfId,
        _id: item._id
      }));
    }
    
    // If we have a single shelf item with serial number
    if (this.data.shelf.serialNumber) {
      return [{
        serialNumber: this.data.shelf.serialNumber,
        shelfCode: this.data.shelf.shelfCode,
        barcode: this.data.shelf.barcode,
        quantity: this.data.shelf.quantity || 0
      }];
    }
    
    // Default fallback - create a single item
    return [{
      serialNumber: `SN-${Date.now()}`,
      shelfCode: this.data.shelf.shelve || 'Unknown',
      barcode: undefined,
      quantity: this.data.shelf.quantity || this.data.shelf.allocatedQuantity || 0
    }];
  }

  private generateSerialNumber(index: number): string {
    // Generate a unique serial number based on shelf info and index
    const shelfCode = this.data.shelf.shelve || 'SH';
    const timestamp = Date.now();
    return `${shelfCode}-${timestamp}-${index + 1}`;
  }

  copyToClipboard(serialNumber: string): void {
    if (navigator.clipboard && window.isSecureContext) {
      // Use the modern Clipboard API
      navigator.clipboard.writeText(serialNumber).then(
        () => {
          console.log('Serial number copied to clipboard successfully');
          // You could add a toast notification here if needed
        },
        (err) => {
          console.error('Failed to copy serial number: ', err);
          this.fallbackCopyTextToClipboard(serialNumber);
        }
      );
    } else {
      // Fallback for older browsers
      this.fallbackCopyTextToClipboard(serialNumber);
    }
  }

  private fallbackCopyTextToClipboard(text: string): void {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    
    // Avoid scrolling to bottom
    textArea.style.top = '0';
    textArea.style.left = '0';
    textArea.style.position = 'fixed';

    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand('copy');
      if (successful) {
        console.log('Serial number copied to clipboard using fallback method');
      } else {
        console.error('Fallback: Oops, unable to copy');
      }
    } catch (err) {
      console.error('Fallback: Oops, unable to copy', err);
    }

    document.body.removeChild(textArea);
  }

  onCancel(): void {
    this.dialogRef.close({
      quantitiesUpdated: this.quantitiesUpdated
    });
  }

  onPrint(item: BarcodeItem): void {
    this.dialogRef.close({
      print: true,
      item: item,
      variantId: this.data.variantId
    });
  }

  onPrintAll(): void {
    this.dialogRef.close({
      print: true,
      items: this.getBarcodeItems(),
      variantId: this.data.variantId
    });
  }

  startEdit(item: BarcodeItem): void {
    this.editingItem = { ...item };
  }

  cancelEdit(): void {
    this.editingItem = null;
  }

  validateQuantity(item: BarcodeItem): boolean {
    const originalQty = this.originalQuantities[item.serialNumber] || 0;
    const newQty = item.quantity || 0;
    
    // if (newQty > originalQty) {
    //   this.toasterService.errToastr(this.translateService.instant('QuantityCannotBeIncreased'));
    //   return false;
    // }
    
    if (newQty < 0) {
      this.toasterService.errToastr(this.translateService.instant('QuantityCannotBeNegative'));
      return false;
    }
    
    return true;
  }

  async submitQuantityUpdate(): Promise<void> {
    if (!this.editingItem || !this.validateQuantity(this.editingItem)|| !this.editingItem?.shelfId) {
      return;
    }
    this.spinnerService.addToLoader('updateQuantity');
    
    try {
      const inventoryId = this.data.variantId
      const shelvesId = this.editingItem._id || ''
      
      const payload = { quantity: this.editingItem.quantity || 0,reason: this.editingItem.reason || '', shelfCode: this.editingItem.shelfCode, serialNumber: this.editingItem.serialNumber };
      console.log('payload', payload);
      await lastValueFrom(this.productService.updateInventoryShelfQuantity(inventoryId, shelvesId, payload));
      
      this.originalQuantities[this.editingItem.serialNumber] = this.editingItem.quantity || 0;
      
      const itemIndex = this.barcodeItems.findIndex(item => item.serialNumber === this.editingItem!.serialNumber);
      if (itemIndex !== -1) {
        this.barcodeItems[itemIndex].quantity = this.editingItem!.quantity;
      }
      
      this.quantitiesUpdated = true;
      this.toasterService.successToastr(this.translateService.instant('QuantityUpdatedSuccessfully'));
      this.editingItem = null;
      
    } catch (error) {
      console.error('Error updating quantity:', error);
      this.toasterService.errToastr(this.translateService.instant('FailedToUpdateQuantity'));
    } finally {
      this.spinnerService.removeFromLoader('updateQuantity');
    }
  }

  isEditing(item: BarcodeItem): boolean {
    const isEditing = this.editingItem?.serialNumber === item.serialNumber;
    console.log(`Checking if editing ${item.serialNumber}: ${isEditing}`);
    return isEditing;
  }

  decreaseQuantity(): void {
    if (this.editingItem && this.editingItem.quantity && this.editingItem.quantity > 0) {
      this.editingItem.quantity--;
    }
  }

  increaseQuantity(): void {
    if (this.editingItem && this.editingItem.quantity !== undefined) {
      const maxQuantity = this.originalQuantities[this.editingItem.serialNumber] || 0;
      // if (this.editingItem.quantity < maxQuantity) {
        this.editingItem.quantity++;
      // }
    }
  }

  onQuantityChange(): void {
  }

  onQuantityKeyDown(event: KeyboardEvent): void {
    if ([46, 8, 9, 27, 13, 110, 190].indexOf(event.keyCode) !== -1 ||
      (event.keyCode === 65 && event.ctrlKey === true) ||
      (event.keyCode === 67 && event.ctrlKey === true) ||
      (event.keyCode === 86 && event.ctrlKey === true) ||
      (event.keyCode === 88 && event.ctrlKey === true) ||
      (event.keyCode >= 35 && event.keyCode <= 39)) {
      return;
    }
    if ((event.shiftKey || (event.keyCode < 48 || event.keyCode > 57)) &&
      (event.keyCode < 96 || event.keyCode > 105)) {
      event.preventDefault();
    }
  }
} 